import { PermissionGate } from "@components/PermissionGate";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import i18n from "src/i18n";
import { z } from "zod";

import { MultiSelectWithSearch } from "@components/MultiSelectWithSearch";

import { useCreatePromoCode } from "@hooks/mutations/promocodes/useCreatePromoCode";
import { useGetClinicConsultationServices } from "@hooks/queries/clinic/useGetClinicConsultationServices";
import { useGetDoctorsForConsultationService } from "@hooks/queries/user/useGetDoctorsForConsultationService";

import { StyledAddPromoCodeModalContent } from "./AddPromoCodeModal.styled";


const addFormSchema = () =>
  z
    .object({
      code: z.string().min(1, i18n.t("code-required")),
      discountPercent: z
        .number()
        .min(1, i18n.t("discount-required"))
        .max(100, i18n.t("discount-max-100")),
      validFrom: z.date({
        required_error: i18n.t("valid-from-required"),
        invalid_type_error: i18n.t("valid-from-invalid"),
      }),
      validUntil: z.date({
        required_error: i18n.t("valid-until-required"),
        invalid_type_error: i18n.t("valid-until-invalid"),
      }),
      services: z
        .array(
          z.object({
            label: z.string(),
            value: z.string(),
          })
        )
        .min(1, i18n.t("services-required")),
      medicalProfessional: z.string().optional(),
      maxUses: z.number(),
    })
    .refine(data => data.validUntil > data.validFrom, {
      message: i18n.t("valid-until-must-be-after-valid-from"),
      path: ["validUntil"],
    });

type AddFormInputs = z.infer<ReturnType<typeof addFormSchema>>;

interface AddPromoCodeModalProps {
  opened: boolean;
  close: () => void;
}

export const AddPromoCodeModal = ({
  opened,
  close,
}: AddPromoCodeModalProps) => {
  const { t } = useTranslation();

  const { mutate: createPromoCode, isLoading: isCreatePromoCodeLoading } =
    useCreatePromoCode();

  // Fetch services data following the same pattern as AddDoctorModal
  const { data: servicesData } = useGetClinicConsultationServices();

  // Fetch doctors data following the same pattern as AddDoctorModal
  const { data: doctorsData } = useGetDoctorsForConsultationService({
    role: "doctor",
    accountOptions: true,
    isEnabled: opened,
  });

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(addFormSchema()),
    initialValues: {
      code: "",
      discountPercent: 0,
      validFrom: new Date(),
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      services: [] as { label: string; value: string }[],
      medicalProfessional: "",
      maxUses: 1,
    },
  });

  // Reset form when modal is closed
  useEffect(() => {
    if (!opened) {
      form.reset();
    }
  }, [opened]);

  const handleFormSubmit = (values: AddFormInputs) => {
    // Convert services from ComboboxItem[] to service IDs for API
    const serviceIds = values.services.map(service => service.value);

    // Get selected doctor name (not ID) for API - optional
    let doctorName = "";
    if (values.medicalProfessional) {
      const selectedDoctor = doctorsData?.find(
        doctor => doctor.value === values.medicalProfessional
      );
      doctorName = selectedDoctor
        ? selectedDoctor.label
        : values.medicalProfessional;
    }

    createPromoCode(
      {
        ...values,
        services: serviceIds, // Send service IDs, not names
        medicalProfessional: doctorName, // Send doctor name (empty string if not selected)
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-promo-code")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="md">
      <StyledAddPromoCodeModalContent>
        <form
          className="form"
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <div className="form-field">
            <TextInput
              label={t("code")}
              withAsterisk
              {...form.getInputProps("code")}
              key={form.key("code")}
              placeholder={t("eg", { example: "SAVE20" })}
            />
          </div>

          <div className="form-field">
            <NumberInput
              label={t("discount-percent")}
              withAsterisk
              {...form.getInputProps("discountPercent")}
              key={form.key("discountPercent")}
              placeholder={t("eg", { example: "20" })}
              min={1}
              max={100}
              suffix="%"
            />
          </div>

          <div className="date-range-container">
            <div className="form-field">
              <DateInput
                label={t("valid-from")}
                withAsterisk
                {...form.getInputProps("validFrom")}
                key={form.key("validFrom")}
                placeholder={t("select-date")}
                minDate={new Date()}
              />
            </div>

            <div className="form-field">
              <DateInput
                label={t("valid-until")}
                withAsterisk
                {...form.getInputProps("validUntil")}
                key={form.key("validUntil")}
                placeholder={t("select-date")}
                minDate={form.getValues().validFrom || new Date()}
              />
            </div>
          </div>

          <div className="form-field">
            <MultiSelectWithSearch
              label={t("services")}
              placeholder={t("enter-services")}
              data={servicesData || []}
              value={form.getValues().services || []}
              setValue={value => {
                if (typeof value === "function") {
                  form.setFieldValue(
                    "services",
                    value(form.getValues().services || [])
                  );
                } else {
                  form.setFieldValue("services", value);
                }
              }}
              maxDisplayedValues={999}
            />
          </div>

          <div className="form-field">
            <Select
              label={t("medical-professional")}
              {...form.getInputProps("medicalProfessional")}
              key={form.key("medicalProfessional")}
              placeholder={t("select-doctor")}
              data={doctorsData || []}
              searchable
              clearable
            />
          </div>

          <div className="form-field">
            <NumberInput
              label={t("max-uses")}
              {...form.getInputProps("maxUses")}
              key={form.key("maxUses")}
              placeholder={t("eg", { example: "100" })}
              min={1}
            />
          </div>

          <div className="form-actions">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isCreatePromoCodeLoading}>
              {t("cancel")}
            </Button>
            <PermissionGate permission="promo_codes.create"><Button type="submit" loading={isCreatePromoCodeLoading}>
              {t("submit")}
            </Button></PermissionGate>
          </div>
        </form>
      </StyledAddPromoCodeModalContent>
    </Modal>
  );
};
